import React, { useCallback, useState } from 'react';
import { useLoadingBackdrop } from 'hooks/useLoadingBackdrop';
import { message } from 'antd';
import _ from 'lodash';
import { Edit3 as EditIcon } from 'react-feather';

import { Button } from 'ui-kit';

import { coHostUpdate, ICoHost, ICoHostForm, useCoHostList } from 'api/co-hosts';
import CoHostModal from 'components/CoHosts/CoHostModal';
import { flagsToNotificationChannel } from 'utils/notificationChannel';

const EditCoHostButtonModal = ({ coHost }: { coHost: ICoHost }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLoading, hideLoading] = useLoadingBackdrop();

  const [coHostToEdit, setCoHostToEdit] = useState(null);

  const { mutate: updateCoHosts } = useCoHostList();

  const openModal = useCallback(() => {
    setIsOpen(true);
    setCoHostToEdit(coHost);
  }, [coHost]);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    updateCoHosts();
    hideLoading();
  }, [isOpen, updateCoHosts, hideLoading]);

  const onSubmit = useCallback(
    ({ data }: { data: ICoHostForm }) => {
      showLoading();
      if (
        !_.isNull(data.phone_number) &&
        !data.is_request_sms_enabled &&
        !data.is_sales_sms_enabled &&
        !data.is_marketing_sms_enabled
      ) {
        message.error('Select the text info you would like your contact to receive.');
        hideLoading();

        return;
      }

      if (
        _.isNull(data.phone_number) &&
        (data.is_request_sms_enabled || data.is_sales_sms_enabled || data.is_marketing_sms_enabled)
      ) {
        message.error('Phone number is required to receive text info.');
        hideLoading();

        return;
      }

      if (!_.isNull(data.phone_number) && !data.is_sms_enabled && !data.is_whatsapp_enabled) {
        message.error('Select how your contact should receive notifications.');
        hideLoading();

        return;
      }

      coHostUpdate({
        id: data.id,
        phone_number: data.phone_number,
        notification_channel: flagsToNotificationChannel({
          isSmsEnabled: data.is_sms_enabled || false,
          isWhatsappEnabled: data.is_whatsapp_enabled || false,
        }),
        is_sales_sms_enabled: data.is_sales_sms_enabled || false,
        is_request_sms_enabled: data.is_request_sms_enabled || false,
        is_marketing_sms_enabled: data.is_marketing_sms_enabled || false,
      })
        .then(() => {
          message.success('Co-host contact updated successfully!');
          updateCoHosts();
          closeModal();
          hideLoading();
        })
        .catch((error) => {
          message.error(error.message || 'Error updating co-host');
          hideLoading();
        });
    },
    [showLoading, hideLoading, closeModal, updateCoHosts],
  );

  return (
    <>
      <Button variant="contained" onClick={openModal} size="small">
        <EditIcon />
      </Button>
      <CoHostModal
        initialData={coHostToEdit}
        isOpen={isOpen}
        onConfirm={onSubmit}
        onCancel={closeModal}
      />
    </>
  );
};

export default EditCoHostButtonModal;
